import { useEffect, useState } from "react";
import type { MultiStoreDailySummary, MultiStoreSalesTrend } from "@ai-pos/shared";
import { dailySummaryApi } from "../api/resources.js";
import { TrendChart } from "../components/TrendChart.js";
import { addDaysToDate, formatShortDate } from "../utils/date.js";

export function AllStoresSummary() {
  const [businessDate, setBusinessDate] = useState("");
  const [summary, setSummary] = useState<MultiStoreDailySummary | null>(null);
  const [trendStart, setTrendStart] = useState("");
  const [trendEnd, setTrendEnd] = useState("");
  const [trend, setTrend] = useState<MultiStoreSalesTrend | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTrend = (start: string, end: string) => {
    if (!start || !end) return;
    dailySummaryApi
      .getMultiStoreTrend(start, end)
      .then((res) => setTrend(res.trend))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "推移の取得に失敗しました"));
  };

  const load = (date?: string) => {
    dailySummaryApi
      .getMultiStore(date || undefined)
      .then((res) => {
        setSummary(res.summary);
        setBusinessDate(res.summary.businessDate);
        if (!trendStart && !trendEnd) {
          const defaultStart = addDaysToDate(res.summary.businessDate, -6);
          setTrendStart(defaultStart);
          setTrendEnd(res.summary.businessDate);
          loadTrend(defaultStart, res.summary.businessDate);
        }
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => load(), []);

  return (
    <div className="stack">
      <h2>全店舗集計（本部・オーナー用）</h2>
      <div className="card row">
        <div className="form-field">
          <label htmlFor="summary-date">営業日</label>
          <input id="summary-date" type="date" value={businessDate} onChange={(e) => setBusinessDate(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={() => load(businessDate)}>
          表示
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {summary && (
        <>
          <div className="grid-2">
            <div className="card">
              <div style={{ color: "var(--color-text-muted)" }}>合計 総売上</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>¥{summary.combined.totalSales.toLocaleString("ja-JP")}</div>
            </div>
            <div className="card">
              <div style={{ color: "var(--color-text-muted)" }}>合計 会計件数</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.combined.orderCount}件</div>
            </div>
            <div className="card">
              <div style={{ color: "var(--color-text-muted)" }}>合計 客数</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{summary.combined.guestCount}名</div>
            </div>
            <div className="card">
              <div style={{ color: "var(--color-text-muted)" }}>合計 客単価</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                ¥{summary.combined.averageSpendPerGuest.toLocaleString("ja-JP")}
              </div>
            </div>
          </div>

          <div className="card">
            <h3>店舗別比較</h3>
            <table>
              <thead>
                <tr>
                  <th>店舗</th>
                  <th>総売上</th>
                  <th>会計件数</th>
                  <th>客数</th>
                  <th>客単価</th>
                  <th>値引き額</th>
                  <th>取消金額</th>
                </tr>
              </thead>
              <tbody>
                {summary.stores.map((store) => (
                  <tr key={store.storeId}>
                    <td>{store.storeName}</td>
                    <td>¥{store.summary.totalSales.toLocaleString("ja-JP")}</td>
                    <td>{store.summary.orderCount}件</td>
                    <td>{store.summary.guestCount}名</td>
                    <td>¥{store.summary.averageSpendPerGuest.toLocaleString("ja-JP")}</td>
                    <td>¥{store.summary.discountTotal.toLocaleString("ja-JP")}</td>
                    <td>¥{store.summary.voidedTotal.toLocaleString("ja-JP")}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700 }}>
                  <td>合計</td>
                  <td>¥{summary.combined.totalSales.toLocaleString("ja-JP")}</td>
                  <td>{summary.combined.orderCount}件</td>
                  <td>{summary.combined.guestCount}名</td>
                  <td>¥{summary.combined.averageSpendPerGuest.toLocaleString("ja-JP")}</td>
                  <td>¥{summary.combined.discountTotal.toLocaleString("ja-JP")}</td>
                  <td>¥{summary.combined.voidedTotal.toLocaleString("ja-JP")}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card row" style={{ alignItems: "flex-end" }}>
            <h3 style={{ margin: 0, marginRight: "auto" }}>推移グラフの表示期間</h3>
            <div className="form-field">
              <label htmlFor="trend-start">開始日</label>
              <input
                id="trend-start"
                type="date"
                value={trendStart}
                max={trendEnd || undefined}
                onChange={(e) => setTrendStart(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="trend-end">終了日</label>
              <input
                id="trend-end"
                type="date"
                value={trendEnd}
                min={trendStart || undefined}
                onChange={(e) => setTrendEnd(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" onClick={() => loadTrend(trendStart, trendEnd)}>
              表示
            </button>
          </div>

          {trend && (
            <div className="stack">
              <h3>全店舗合計の推移</h3>
              <TrendChart
                title="来客数"
                points={trend.combined.map((p) => ({ label: formatShortDate(p.businessDate), value: p.guestCount }))}
                color="var(--color-primary)"
                formatValue={(v) => `${v}名`}
              />
              <TrendChart
                title="売上"
                points={trend.combined.map((p) => ({ label: formatShortDate(p.businessDate), value: p.totalSales }))}
                color="var(--color-primary-dark)"
                formatValue={(v) => `¥${v.toLocaleString("ja-JP")}`}
              />
              <TrendChart
                title="客単価"
                points={trend.combined.map((p) => ({
                  label: formatShortDate(p.businessDate),
                  value: p.averageSpendPerGuest,
                }))}
                color="var(--color-warning)"
                formatValue={(v) => `¥${v.toLocaleString("ja-JP")}`}
              />

              {trend.stores.map((store) => (
                <div key={store.storeId} className="stack">
                  <h3>{store.storeName}の推移</h3>
                  <TrendChart
                    title="来客数"
                    points={store.points.map((p) => ({ label: formatShortDate(p.businessDate), value: p.guestCount }))}
                    color="var(--color-primary)"
                    formatValue={(v) => `${v}名`}
                  />
                  <TrendChart
                    title="売上"
                    points={store.points.map((p) => ({ label: formatShortDate(p.businessDate), value: p.totalSales }))}
                    color="var(--color-primary-dark)"
                    formatValue={(v) => `¥${v.toLocaleString("ja-JP")}`}
                  />
                  <TrendChart
                    title="客単価"
                    points={store.points.map((p) => ({
                      label: formatShortDate(p.businessDate),
                      value: p.averageSpendPerGuest,
                    }))}
                    color="var(--color-warning)"
                    formatValue={(v) => `¥${v.toLocaleString("ja-JP")}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="card">
            <h3>商品別売上（全店舗合算）</h3>
            <table>
              <thead>
                <tr>
                  <th>商品</th>
                  <th>数量</th>
                  <th>金額</th>
                </tr>
              </thead>
              <tbody>
                {summary.combined.salesByProduct.map((p) => (
                  <tr key={p.productId}>
                    <td>{p.productName}</td>
                    <td>{p.quantity}</td>
                    <td>¥{p.amount.toLocaleString("ja-JP")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
